import { useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useParams } from 'react-router-dom'

import { getCenter } from '../../api/centers/get-center'
import { useObserver } from '../../lib/contexts/observer'
import { Center } from '../../lib/types/models/center/center'
import NotFoundPage from '../../views/common/not-found'
import LoadingScreen from '../elements/loading/loading-screen'

export type WithCenterProps = {
  center?: Center
  refetch?: () => Promise<void>
}

// HOC that sets the theme and renders the wrapped component
function withCenter<T extends object>(WrappedComponent: React.ComponentType<T>) {
  return (props: T) => {
    const id = useParams<{ id: string }>().id
    const queryClient = useQueryClient()

    if (!id || isNaN(parseInt(id))) throw new Error('Invalid center ID')
    // Render the wrapped component with the rest of the props
    const { data, isPending, refetch } = useQuery({
      queryKey: ['centers', id],
      queryFn: () => getCenter(parseInt(id)),
      enabled: !!id && !isNaN(parseInt(id))
    })

    useObserver({
      key: 'ol-center-data',
      update: () => {
        queryClient.invalidateQueries({ queryKey: ['centers', id] })
      }
    })

    if (isPending) return <LoadingScreen />
    // the request failed (e.g. 404 on an unknown center id): never leave the page spinning
    if (!data) return <NotFoundPage />
    return <WrappedComponent center={data} refetch={refetch} {...props} />
  }
}

export default withCenter
